import { useTellescopeMutation, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import type { EnduserSession } from "@tellescope/sdk";

export interface UnresolvedCartItem {
  stripePriceId: string;
  quantity: number;
}

/**
 * Resolves each cart item's selected Stripe price ID to its Tellescope Product _id via
 * reverse-lookup (products.getSome), then calls prepare_stripe_checkout with the resolved _ids.
 * Run at the point checkout is initiated, not earlier, since a resolved _id could go stale if
 * held longer. Keyed by stripePriceId, not stripeProductId — each price variant of a product maps
 * to its own distinct Tellescope Product record, so the specific variant the enduser selected is
 * what determines which Product gets charged.
 *
 * stripeKey (the shop block's configured Stripe Custom account, if any) is passed through
 * unconditionally, even when undefined — the backend auto-resolves the account when omitted (the
 * enduser's saved key, or the org's sole integration), erroring only if that's ambiguous.
 *
 * session is passed in rather than read from an app-local store — this package doesn't own the
 * enduser session, the consuming app (portal-web) does, so the caller (ShopBlock.tsx, via
 * PaymentSummary) resolves and passes it down.
 */
export const usePrepareShopCheckout = (session: EnduserSession | null) => {
  const queryClient = useTellescopeQueryClient(session ?? undefined);

  const checkoutMutation = useTellescopeMutation({
    model: "products",
    method: "prepare_stripe_checkout",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "disabled",
  });

  const resolveAndPrepareCheckout = async (items: UnresolvedCartItem[], stripeKey: string | undefined) => {
    const unresolved: string[] = [];
    const productIds: string[] = [];

    for (const item of items) {
      const result = await queryClient.fetchInfiniteQuery({
        model: "products",
        method: "getSome",
        request: { filter: { stripePriceId: item.stripePriceId }, limit: 1 },
        initialPageParam: undefined,
        getNextPageParam: () => undefined,
      });
      const match = result.pages[0]?.[0];
      if (!match) {
        unresolved.push(item.stripePriceId);
        continue;
      }
      for (let i = 0; i < item.quantity; i++) {
        productIds.push(match.id);
      }
    }

    if (unresolved.length > 0) {
      throw new Error(`No matching product configured for: ${unresolved.join(", ")}`);
    }

    return checkoutMutation.mutateAsync({ productIds, stripeKey });
  };

  return { ...checkoutMutation, resolveAndPrepareCheckout };
};
